import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Flex, Tabs as PillTab, TabsContainer, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AppDispatch, RootState } from 'store'
import { getKitchenSuggestions } from 'features/food/foodSlice'
import { IKitchenSuggestionResponse } from 'types/api/foodApi.interface'

export const SuggestionsTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { logisticsKitchens, kitchenSuggestions, isLoading } = useSelector(
    (store: RootState) => store.food,
  )
  const kitchens = logisticsKitchens.data || []
  const [kitchenId, setKitchenId] = useState<number | null>(null)

  useEffect(() => {
    if (kitchenId == null && kitchens[0]) setKitchenId(kitchens[0].id)
  }, [kitchens, kitchenId])

  useEffect(() => {
    if (kitchenId == null) return
    dispatch(getKitchenSuggestions({ kitchenId }))
  }, [dispatch, kitchenId])

  const kitchen = kitchens.find((k) => k.id === kitchenId)
  const rows = (kitchenSuggestions || []).map((s, idx) => ({
    ...s,
    id: s.dish?.id ?? idx,
    rank: idx + 1,
  }))

  const columns: IHonestTableColumn<IKitchenSuggestionResponse & { id: number; rank: number }>[] = [
    {
      id: 'rank',
      header: 'Rank',
      cell: (r) => (
        <BodyText weight='700' style={{ color: r.rank === 1 ? colors.orange : undefined }}>
          #{r.rank}
        </BodyText>
      ),
    },
    {
      id: 'dish',
      header: 'Dish',
      sticky: true,
      minWidth: '12rem',
      cell: (r) => (
        <Cell>
          <BodyText weight='600'>{r.dish?.name || '—'}</BodyText>
          <Flex wrap gap='0.25rem' mt='4'>
            {(r.dish?.dietaryTags || []).map((t) => (
              <StatusPill key={t.code || t.id} status='accent'>
                {t.code || t.name}
              </StatusPill>
            ))}
          </Flex>
        </Cell>
      ),
    },
    {
      id: 'cookable',
      header: 'Cookable portions',
      cell: (r) => (
        <BodyText weight='700' style={{ fontSize: '1rem' }}>
          {r.cookablePortions}
        </BodyText>
      ),
    },
    {
      id: 'why',
      header: 'Why',
      minWidth: '14rem',
      cell: (r) => (
        <OtherText
          style={{
            color:
              r.daysToSoonestExpiry != null && r.daysToSoonestExpiry <= 2
                ? colors.red
                : r.daysToSoonestExpiry != null && r.daysToSoonestExpiry <= 5
                  ? colors.orange
                  : undefined,
          }}
        >
          {(r.expiringIngredients || []).length
            ? `Expiring: ${(r.expiringIngredients || []).join(', ')}`
            : '—'}
          {r.daysToSoonestExpiry != null ? ` · ${r.daysToSoonestExpiry}d to soonest expiry` : ''}
          {r.limitingIngredient ? ` · limited by ${r.limitingIngredient}` : ''}
        </OtherText>
      ),
    },
  ]

  if (!kitchens.length) {
    return <OtherText grey>No kitchens configured yet.</OtherText>
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <TabsContainer>
        {kitchens.map((k) => (
          <PillTab key={k.id} active={kitchenId === k.id} onClick={() => setKitchenId(k.id)}>
            {k.name}
          </PillTab>
        ))}
      </TabsContainer>
      <Container>
        <ContainerHeader>
          <TitleText weight='700'>
            What to cook first — {kitchen?.name || 'Kitchen'} (FEFO pressure ranking)
          </TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={columns}
            data={rows}
            keyField='id'
            loading={isLoading}
            emptyMessage='No suggestions for this kitchen'
          />
        </ContainerContent>
      </Container>
    </Flex>
  )
}
